import React from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import updatesPanelStore from "Stores/updatesPanelStore";
import "./updatePopup.scss";

// Post titles lead with an emoji ("📞 Power dialer: ..."); it becomes the hero
// when the post has no featured image, and the title reads clean without it.
const splitTitle = title => {
  const m = (title || "").match(/^([^\w\s]+)\s+(.+)$/);
  return m ? { emoji: m[1], title: m[2] } : { emoji: "", title: title || "" };
};

/*
 * One product update, opened from the What's new panel: hero (featured image,
 * GIF, or the post's emoji), title, summary, what changed, and "Try it now"
 * into the app. Same content as the WordPress post, so nothing extra to author;
 * the full post stays one link away. Escape is handled by the panel.
 */
const UpdatePopup = observer(({ typeTone, areaTone }) => {
  const history = useHistory();
  const p = updatesPanelStore.selected;
  if (!p) return null;

  const { emoji, title } = splitTitle(p.title);

  const tryIt = () => {
    updatesPanelStore.close();
    history.push(p.path);
  };

  return (
    <div className="updatePopup" role="dialog" aria-modal="true" aria-label={title}>
      <div className="updatePopup__overlay" aria-hidden="true" onClick={updatesPanelStore.closePost} />
      <div className="updatePopup__card">
        <button
          type="button"
          className="updatePopup__close"
          aria-label="Close"
          onClick={updatesPanelStore.closePost}
        >
          <i className="material-icons-outlined">close</i>
        </button>

        {p.image ? (
          <div className="updatePopup__media">
            <img src={p.image} alt="" />
          </div>
        ) : (
          <div className="updatePopup__hero" aria-hidden="true">
            <span className="updatePopup__ring updatePopup__ring--outer" />
            <span className="updatePopup__ring updatePopup__ring--inner" />
            <span className="updatePopup__emoji">{emoji || "✨"}</span>
          </div>
        )}

        <div className="updatePopup__meta">
          {p.type && <span className={`updatePopup__chip type-${typeTone[p.type]}`}>{p.type}</span>}
          <span className={`updatePopup__chip tone-${areaTone[p.area] || "blue"}`}>{p.area}</span>
        </div>
        <div className="updatePopup__title">{title}</div>
        {p.summary && <div className="updatePopup__desc">{p.summary}</div>}

        {p.points.length > 0 && (
          <ul className="updatePopup__points">
            {p.points.map(point => (
              <li key={point}>
                <span className="updatePopup__check">
                  <i className="material-icons-outlined">check</i>
                </span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        )}

        {p.path ? (
          <button type="button" className="updatePopup__cta" onClick={tryIt}>
            Try it now
          </button>
        ) : (
          <button type="button" className="updatePopup__cta" onClick={updatesPanelStore.closePost}>
            Got it
          </button>
        )}
        <div className="updatePopup__foot">
          {p.path && (
            <button type="button" className="updatePopup__later" onClick={updatesPanelStore.closePost}>
              Maybe later
            </button>
          )}
          <a className="updatePopup__more" href={p.link} target="_blank" rel="noopener noreferrer">
            Full details
            <i className="material-icons-outlined">open_in_new</i>
          </a>
        </div>
      </div>
    </div>
  );
});

export default UpdatePopup;
